/**
 * Component: sa-video-carousel
 * Simplified video carousel with Swiper
 * Core feature: Auto-play video when slide becomes active
 */
if (!customElements.get('sa-video-carousel')) {
  class SaVideoCarousel extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.userInteracted = false;
    }

    connectedCallback() {
      const swiperContainer = this.querySelector('.sa-video-carousel__swiper');
      if (!swiperContainer) return;

      this.setupUserInteraction();
      this.waitForSwiper(() => this.initSwiper());
    }

    setupUserInteraction() {
      const enableAutoplay = () => {
        if (!this.userInteracted) {
          this.userInteracted = true;
          setTimeout(() => this.playCurrentVideo(), 200);
        }
      };

      this.addEventListener('click', enableAutoplay, { once: true, passive: true });
      this.addEventListener('touchstart', enableAutoplay, { once: true, passive: true });
    }

    waitForSwiper(callback, maxAttempts = 50) {
      let attempts = 0;
      const check = () => {
        if (typeof Swiper !== 'undefined') {
          callback();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(check, 100);
        }
      };
      check();
    }

    initSwiper() {
      const swiperContainer = this.querySelector('.sa-video-carousel__swiper');
      const prevBtn = this.querySelector('.sa-video-carousel__nav-btn--prev');
      const nextBtn = this.querySelector('.sa-video-carousel__nav-btn--next');
      const pagination = this.querySelector('.sa-video-carousel__pagination');

      this.swiper = new Swiper(swiperContainer, {
        slidesPerView: 2,
        centeredSlides: true,
        spaceBetween: 10,
        speed: 600,
        loop: false,
        watchSlidesProgress: true,
        navigation: {
          nextEl: nextBtn,
          prevEl: prevBtn
        },
        pagination: {
          el: pagination,
          clickable: true,
          type: 'bullets'
        },
        breakpoints: {
          320: {
            slidesPerView: 1,
            spaceBetween: 10,
            centeredSlides: false
          },
          768: {
            slidesPerView: 2,
            spaceBetween: 10,
            centeredSlides: true
          },
          1024: {
            slidesPerView: 2,
            spaceBetween: 10,
            centeredSlides: true
          }
        },
        on: {
          init: () => {
            this.updateButtons();
            // Preload first video immediately
            this.preloadCurrentVideo();
            setTimeout(() => this.playCurrentVideo(true), 300);
          },
          slideChange: () => {
            this.updateButtons();
            this.userInteracted = true;
          },
          slideChangeTransitionStart: () => {
            this.pauseAllVideos();
            // Preload current video immediately when transition starts
            this.preloadCurrentVideo();
          },
          slideChangeTransitionEnd: () => {
            // Play video immediately after transition with minimal delay
            setTimeout(() => this.playCurrentVideo(), 50);
          }
        }
      });
    }

    pauseAllVideos() {
      const slides = this.querySelectorAll('.swiper-slide');
      slides.forEach(slide => {
        const videoElement = slide.querySelector('video-media');
        if (videoElement) {
          // Restore poster visibility when pausing
          const poster = videoElement.querySelector('img');
          if (poster) {
            poster.style.display = '';
            poster.style.opacity = '1';
          }
          
          if (videoElement.hasAttribute('playing')) {
            videoElement.pause();
          }
        }

        // Pause YouTube iframe
        const iframe = slide.querySelector('iframe[src*="youtube.com"]');
        if (iframe && iframe.contentWindow) {
          try {
            iframe.contentWindow.postMessage(
              JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
              'https://www.youtube.com'
            );
          } catch (e) {
            // Ignore errors
          }
        }
      });
    }

    async playCurrentVideo(forceAttempt = false) {
      if (!this.swiper || (!forceAttempt && !this.userInteracted)) return;

      const slides = this.querySelectorAll('.swiper-slide');
      const currentSlide = slides[this.swiper.activeIndex];
      if (!currentSlide) return;

      const videoElement = currentSlide.querySelector('video-media');
      if (!videoElement) return;
      
      // Ensure poster is visible before playing
      const poster = videoElement.querySelector('img');
      if (poster) {
        poster.style.display = '';
        poster.style.opacity = '1';
      }

      if (videoElement.hasAttribute('playing')) return;

      const isYouTube = videoElement.hasAttribute('host') && videoElement.getAttribute('host') === 'youtube';

      if (isYouTube) {
        await this.playYouTubeVideo(videoElement);
      } else {
        await this.playLocalVideo(videoElement);
      }
    }

    preloadCurrentVideo() {
      if (!this.swiper) return;
      
      const slides = this.querySelectorAll('.swiper-slide');
      const currentSlide = slides[this.swiper.activeIndex];
      if (!currentSlide) return;
      
      const videoElement = currentSlide.querySelector('video-media');
      if (!videoElement) return;
      
      const isYouTube = videoElement.hasAttribute('host') && 
                       videoElement.getAttribute('host') === 'youtube';
      
      if (isYouTube) {
        // Pre-load YouTube iframe immediately
        let iframe = videoElement.querySelector('iframe');
        if (!iframe) {
          // Try to trigger loading early
          if (videoElement.player) {
            videoElement.player.catch(() => {});
          } else {
            // Manually extract and load iframe from template
            const template = videoElement.querySelector('template');
            if (template && template.content) {
              const iframeTemplate = template.content.querySelector('iframe');
              if (iframeTemplate) {
                iframe = iframeTemplate.cloneNode(true);
                let src = iframe.src;
                src = src.replace(/[?&]autoplay=\d+/g, '').replace(/[?&]mute=\d+/g, '');
                const separator = src.includes('?') ? '&' : '?';
                iframe.src = src + separator + 'mute=1&iv_load_policy=3&cc_load_policy=0';
                template.replaceWith(iframe);
              }
            }
          }
        } else {
          // Iframe already exists, ensure it's ready
          if (!iframe.src.includes('mute=1')) {
            let src = iframe.src;
            src = src.replace(/[?&]autoplay=\d+/g, '').replace(/[?&]mute=\d+/g, '');
            const separator = src.includes('?') ? '&' : '?';
            iframe.src = src + separator + 'mute=1';
          }
        }
      } else {
        // Pre-trigger local video loading
        if (!videoElement.hasAttribute('loaded')) {
          if (videoElement.player) {
            videoElement.player.catch(() => {});
          }
        }
      }
    }

    async playYouTubeVideo(videoElement) {
      const poster = videoElement.querySelector('img');
      
      // Try player API first
      if (videoElement.player) {
        try {
          const player = await Promise.race([
            videoElement.player,
            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 800))
          ]);
          if (player && typeof player.playVideo === 'function') {
            player.mute();
            player.playVideo();
            // Hide poster quickly after video starts
            setTimeout(() => {
              if (poster) {
                poster.style.opacity = '0';
                setTimeout(() => poster.style.display = 'none', 300);
              }
            }, 400);
            return;
          }
        } catch (e) {
          // Continue to iframe method
        }
      }

      // Use iframe method
      let iframe = videoElement.querySelector('iframe');
      
      // If iframe not loaded yet, wait briefly or create it immediately
      if (!iframe) {
        // Try to create iframe from template immediately
        const template = videoElement.querySelector('template');
        if (template && template.content) {
          const iframeTemplate = template.content.querySelector('iframe');
          if (iframeTemplate) {
            iframe = iframeTemplate.cloneNode(true);
            let src = iframe.src;
            src = src.replace(/[?&]autoplay=\d+/g, '').replace(/[?&]mute=\d+/g, '');
            const separator = src.includes('?') ? '&' : '?';
            iframe.src = src + separator + 'mute=1&iv_load_policy=3&cc_load_policy=0';
            template.replaceWith(iframe);
          }
        }
        
        // Wait a bit if still not loaded
        if (!iframe) {
          await new Promise(resolve => setTimeout(resolve, 50));
          iframe = videoElement.querySelector('iframe');
        }
      }

      if (iframe) {
        // Update URL with autoplay
        let src = iframe.src;
        if (!src.includes('autoplay=1')) {
          src = src.replace(/[?&]autoplay=\d+/g, '').replace(/[?&]mute=\d+/g, '');
          const separator = src.includes('?') ? '&' : '?';
          iframe.src = src + separator + 'autoplay=1&mute=1';
          
          iframe.addEventListener('load', () => {
            setTimeout(() => {
              this.sendYouTubeCommand(iframe, 'playVideo');
              // Hide poster quickly after video loads
              if (poster) {
                setTimeout(() => {
                  poster.style.opacity = '0';
                  setTimeout(() => poster.style.display = 'none', 300);
                }, 500);
              }
            }, 100);
          }, { once: true });
        } else {
          // Iframe already has autoplay, play immediately
          this.sendYouTubeCommand(iframe, 'playVideo');
          if (poster) {
            setTimeout(() => {
              poster.style.opacity = '0';
              setTimeout(() => poster.style.display = 'none', 300);
            }, 600);
          }
        }
      } else if (poster) {
        // If iframe still not loaded, keep poster visible to prevent white screen
      }
    }

    sendYouTubeCommand(iframe, command) {
      if (!iframe || !iframe.src || !iframe.src.includes('youtube.com')) return;

      const trySend = (attempt = 0) => {
        if (attempt >= 3) return;

        setTimeout(() => {
          if (!iframe || !iframe.contentWindow) {
            if (attempt < 2) trySend(attempt + 1);
            return;
          }

          try {
            // Verify iframe is ready
            const origin = new URL(iframe.src).origin;
            if (origin.includes('youtube.com')) {
              iframe.contentWindow.postMessage(
                JSON.stringify({ event: 'command', func: command, args: '' }),
                'https://www.youtube.com'
              );
            }
          } catch (e) {
            // Retry if not a security error
            if (attempt < 2 && e.name !== 'SecurityError') {
              trySend(attempt + 1);
            }
          }
        }, attempt === 0 ? 150 : 300);
      };

      trySend(0);
    }

    async playLocalVideo(videoElement) {
      const poster = videoElement.querySelector('img');
      
      // Load video if not loaded
      if (!videoElement.hasAttribute('loaded')) {
        if (videoElement.player) {
          await videoElement.player.catch(() => {});
        } else {
          const playButton = videoElement.querySelector('.deferred-poster, .play-button');
          if (playButton) {
            playButton.click();
            await new Promise(resolve => setTimeout(resolve, 400));
          }
        }
      }

      // Play video
      const videoTag = videoElement.querySelector('video');
      if (videoTag) {
        videoTag.muted = true;
        const playPromise = videoTag.play();
        
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.then(() => {
            // Hide poster after video starts playing
            if (poster) {
              setTimeout(() => {
                poster.style.opacity = '0';
                setTimeout(() => poster.style.display = 'none', 300);
              }, 300);
            }
          }).catch(() => {
            // Autoplay failed, keep poster visible
          });
        }
      } else if (videoElement.play && typeof videoElement.play === 'function') {
        const playResult = videoElement.play();
        if (playResult && typeof playResult.then === 'function') {
          playResult.then(() => {
            if (poster) {
              setTimeout(() => {
                poster.style.opacity = '0';
                setTimeout(() => poster.style.display = 'none', 300);
              }, 300);
            }
          }).catch(() => {});
        }
      }
    }

    updateButtons() {
      if (!this.swiper) return;
      const prevBtn = this.querySelector('.sa-video-carousel__nav-btn--prev');
      const nextBtn = this.querySelector('.sa-video-carousel__nav-btn--next');
      if (prevBtn) prevBtn.classList.toggle('sa-video-carousel__nav-btn--disabled', this.swiper.isBeginning);
      if (nextBtn) nextBtn.classList.toggle('sa-video-carousel__nav-btn--disabled', this.swiper.isEnd);
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  customElements.define('sa-video-carousel', SaVideoCarousel);
}
